import { Injectable, signal } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../environments/environment';
import { Subject } from 'rxjs';

/**
 * Payload interface for videoAdded events
 */
export interface VideoAddedPayload {
  videoId: number;
  youtubeId: string;
  title: string;
  channel: string;
  duration: number;
  views: number;
  uploadedAt: string;
  position: number;
  nickname: string;
}

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  private socket: Socket | null = null;
  private registeredEvents = new Set<string>();

  public connected = signal(false);
  public message = signal('');

  private videoAddedSubject = new Subject<VideoAddedPayload>();
  public videoAdded$ = this.videoAddedSubject.asObservable();

  private nowPlayingSubject = new Subject<VideoAddedPayload>();
  public nowPlaying$ = this.nowPlayingSubject.asObservable();

  // public videoAdded = signal<VideoAddedPayload | null>(null);
  // public nowPlaying = signal<VideoAddedPayload | null>(null);

  /**
   * Initialize the WebSocket connection and set up base listeners
   * @param url - WebSocket server URL
   */
  connect() {
    if (this.socket) return; // already connected

    const socketUrl = new URL(environment.socketUrl);

    this.socket = io(socketUrl.origin, {
      path: socketUrl.pathname,
      transports: ['websocket'],
    });

    this.socket.on('connect', () => {
      this.connected.set(true);
      console.log('✅ WS connected');
    });

    this.socket.on('disconnect', () => {
      this.connected.set(false);
      console.log('❌ WS disconnected');
    });

    this.socket.on('connect_error', error => {
      this.connected.set(false);
      console.error('Socket connection failed:', error.message);
    });

    // Generic message listener
    this.socket.on('message', (data: { message: string }) => {
      this.message.set(data.message);
      console.log('📩 Received message:', data);
    });
  }

  /**
   * Listen for videoAdded events for a specific playlist
   * @param playlistId - ID of the playlist to subscribe to
   */
  onVideoAdded(playlistId: number = 1) {
    if (!this.socket) return;
    const eventName = `playlist:${playlistId}:videoAdded`;
    if (this.registeredEvents.has(eventName)) return;
    this.registeredEvents.add(eventName);
    this.socket.on(eventName, (payload: VideoAddedPayload) => {
      // this.videoAdded.set(payload);
      this.videoAddedSubject.next(payload);
      console.log(`📩 Received ${eventName}:`, payload);
    });
  }

  onNowPlaying(playlistId: number = 1) {
    if (!this.socket) return;
    const eventName = `playlist:${playlistId}:nowPlaying`;
    if (this.registeredEvents.has(eventName)) return;
    this.registeredEvents.add(eventName);
    this.socket.on(eventName, (video: VideoAddedPayload) => {
      this.nowPlayingSubject.next(video);
      // this.nowPlaying.set(video);
    });
  }

  /** Gracefully close the WebSocket connection */
  disconnect() {
    if (!this.socket) return;
    this.socket.disconnect();
    this.socket = null;
    this.registeredEvents.clear();
    this.connected.set(false);
    console.log('🛑 WS connection closed');
  }
}
